import { Component, HostListener, signal, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef, OnInit, OnDestroy, Output, EventEmitter, effect, computed, HostBinding } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterModule, ActivatedRoute } from '@angular/router';
import { ThemeService } from '../../core/services/theme/theme.service';
import { NotificationDropdownComponent } from '../../shared/components/notification-dropdown/notification-dropdown';
import { AuthService } from '../../core/services/Auth/auth-service';
import { NavigationService, NavigationItem } from '../../core/services/sidebar/sidebar.service';
import { Subscription } from 'rxjs';
import { LayoutService } from '../../core/services/layout/layout.service';
import { ApiService } from '@app/core/services/API/api-service';
import { ClientSummarySlider } from '../../layout/client-summary-slider';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RecentItemsService } from '@app/shared/services/recent-items.service';
import { ThemeManagementService } from '@app/core/services/theme/theme-management.service';
import { UserSettings } from '@app/shared/components/my-settings/my-settings';
import { UserSettingsService } from '@app/shared/services/user-settings.service';
import { NotificationService } from '@app/core/services/notifications/notification.service';
import { SearchCategoryService } from '@app/core/services/search/search-category.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    NotificationDropdownComponent,
    ClientSummarySlider,
    MatIconModule,
    MatTooltipModule
  ],
  templateUrl: './header.html',
  styleUrls: ['./header.scss']
})
export class Header implements OnInit, OnDestroy {

  @ViewChild('itemsContainer') itemsContainer!: ElementRef;
  
  // Add class to host element when client summary slider is open
  @HostBinding('class.client-summary-slider-open') get isClientSummaryOpen() {
    return this.showClientSummarySlider;
  }

  navActive = false;
  showWidgetsBtn = false;
  isMainPage = false; // For header navigation highlighting
  dropdownOpen = signal(false);
  notificationsOpen = signal(false);
  searchDropdownOpen = signal(false);
  moreDropdownOpen = signal(false);
  isDropdownOpen = false;

  searchFocused = false;
  searchQuery = signal('');
  selectedSearchOption = signal('All');
  showRecentArrows = false;
  canScrollLeft = false;
  canScrollRight = false;
  recentlyViewedExpanded = signal(false);
  showClientSummarySlider = false;
  showClientSummaryButton = false;
  searchDisabled = signal(false); // Enable search functionality


  // recentItems = signal([
  //   'Project A - Task 1 | sgkghfsk shjdfyuewur ueyruieyru',
  //   'Portfolio X - Update | sgkghfsk shjdfyuewur ueyruieyru',
  //   'Request 123 - Client Y | sgkghfsk shjdfyuewur ueyruieyru',
  //   'Issue #456 - Bug Fix | sgkghfsk shjdfyuewur ueyruieyru',
  //   'Project B - Meeting Notes | sgkghfsk shjdfyuewur ueyruieyru'
  // ]);
  recentItems = signal<{ _id: string, projectName: string }[]>([]);
  headerItems: NavigationItem[] = [];
  activeHeader: NavigationItem | null = null;
  private subscription = new Subscription();
  allowedRoutes = ['/tasks'];

  isSidebarCollapsed = false;
  userName: string | null = null;
  userId: string | null = null;
  userImage: string | null = null;
  profileimage: string | null = null;

  @Output() changePasswordClick = new EventEmitter<void>();
  @Output() backgroundImageSelected = new EventEmitter<string>();
  @Output() backgroundImagePreview = new EventEmitter<string>();
  @Output() themeBtnClick = new EventEmitter<void>();
  @Output() settingsClick = new EventEmitter<void>();
  @Output() profileDropdownStateChange = new EventEmitter<{ isOpen: boolean, userName: string | null, userImage: string | null }>();

  // Add properties to track cleanup
  private scrollListener: (() => void) | null = null;
  private clickListeners: Array<{ element: Document | Element, event: string, handler: EventListener }> = [];
  // Add private property for storage listener
  private _storageListener?: (event: StorageEvent) => void;
  unreadNotifications = computed(() => this.notificationService.unseenCount());

  constructor(
    public themeService: ThemeService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private navigationService: NavigationService,
    public layoutService: LayoutService,
     private recentItemsService: RecentItemsService,
     private themeManagementService: ThemeManagementService,
     private userSettingsService: UserSettingsService,
     public notificationService: NotificationService,
     private searchCategoryService: SearchCategoryService
  ) {
    this.subscription.add(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
        this.showWidgetsBtn = event.urlAfterRedirects.includes('/dashboard');
        this.isMainPage = event.urlAfterRedirects === '/' || 
                         event.urlAfterRedirects === '/dashboard' || 
                         event.urlAfterRedirects === '/projects' || 
                         event.urlAfterRedirects.startsWith('/projects/');
        
        // Handle client summary button visibility
        this.updateClientSummaryButtonVisibility();
        
        // Sync search query from URL params when navigating
        if (event.urlAfterRedirects.includes('/search')) {
          // Get query params from the current route snapshot
          // Use setTimeout to ensure route snapshot is updated
          setTimeout(() => {
            const snapshot = this.route.snapshot;
            const queryParams = snapshot.queryParams;
            if (queryParams['q']) {
              this.searchQuery.set(queryParams['q']);
            } else {
              this.searchQuery.set('');
            }
            this.cdr.detectChanges();
          }, 0);
        } else {
          // Clear search query when navigating away from search page
          this.searchQuery.set('');
        }
        
        // Update scroll state based on route change
        if (event.urlAfterRedirects.includes('/clients') || event.urlAfterRedirects.includes('/clients-list')) {
          this.isScrolled = false;
          this.isClientPageScrolled = false; // Reset client scroll state
          this.cdr.detectChanges();
        } else {
          // For other routes, check if we need to reset scroll state
          this.updateScrollStateForRoute();
        }
      }
      })
    );
     // Listen for recent items refresh events using Angular signals effect
    effect(() => {
       this.recentItemsService.recentItemsRefreshSignal();
      this.loadRecentItems();
      this.cdr.detectChanges();
    });

    // Listen for search category changes from search page
    effect(() => {
      const category = this.searchCategoryService.selectedCategory();
      if (category !== this.selectedSearchOption()) {
        this.selectedSearchOption.set(category);
        this.cdr.detectChanges();
      }
    });
  }

  ngOnInit(): void {
    this.userName = localStorage.getItem('userName');
    this.profileimage = localStorage.getItem('userImage') || '';
    this.userId = localStorage.getItem('userID');
    this.userImage = (this.profileimage && this.profileimage.trim()) ? this.profileimage : 'assets/images/avatar.png';
    
    // Check if we're on clients page and set isScrolled accordingly
    this.updateScrollStateForRoute();
    
    // Initialize client summary button visibility based on current route
    this.updateClientSummaryButtonVisibility();
    
    this.subscription.add(
      this.navigationService.getHeaderItems().subscribe(items => {
        this.headerItems = items;
        this.cdr.detectChanges();
      })
    );

    this.subscription.add(
      this.navigationService.getActiveHeader().subscribe(header => {
        this.activeHeader = header;
        this.cdr.detectChanges();
      })
    );

    // Subscribe to query params changes for real-time updates when query params change without route change
    this.subscription.add(
      this.route.queryParams.subscribe(params => {
        // Only sync if we're on the search route
        const currentUrl = this.router.url;
        if (currentUrl.includes('/search')) {
          if (params['q']) {
            this.searchQuery.set(params['q']);
          } else {
            this.searchQuery.set('');
          }
          this.cdr.detectChanges();
        }
      })
    );

    // Cross-tab sync for recently viewed items
    this._storageListener = (event: StorageEvent) => {
      if (
        event.key === 'lastEditedProjectId' ||
        event.key === 'lastEditedProjectName'
      ) {
        this.loadRecentItems();
      } else if (event.key === 'userImage') {
        // Update profile image when changed in another tab
        this.updateUserImage(event.newValue || '');
      }
    };
    window.addEventListener('storage', this._storageListener);
    this.getUserProfiledata(this.userId || undefined);

    this.preloadUserSettings();

    // Initial sync of search query if already on search route (e.g., page refresh)
    if (this.router.url.includes('/search')) {
      const snapshot = this.route.snapshot;
      const queryParams = snapshot.queryParams;
      if (queryParams['q']) {
        this.searchQuery.set(queryParams['q']);
        this.cdr.detectChanges();
      }
    }
  }

  ngAfterViewInit() {
    // Delay the initial check to ensure DOM is fully rendered
    setTimeout(() => {
      this.checkScrollability();
      this.checkForArrows();
      this.setupScrollListener();
      // Ensure scroll state is correct for current route
      this.updateScrollStateForRoute();
      this.cdr.detectChanges();
    }, 100);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();

    // Clean up scroll listener
    if (this.scrollListener) {
      const container = this.itemsContainer?.nativeElement;
      if (container) {
        container.removeEventListener('scroll', this.scrollListener);
      }
    }

    // Clean up all click listeners
    this.clickListeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });
    this.clickListeners = [];



    // Remove storage event listener for cross-tab sync
    if (this._storageListener) {
      window.removeEventListener('storage', this._storageListener);
      this._storageListener = undefined;
    }
  }

  setupScrollListener() {
    const container = this.itemsContainer?.nativeElement;
    if (container) {
      this.scrollListener = () => {
        this.checkScrollability();
        this.cdr.detectChanges();
      };
      container.addEventListener('scroll', this.scrollListener);
    }
  }

  checkScrollability() {
    const container = this.itemsContainer?.nativeElement;
    if (container) {
      this.canScrollLeft = container.scrollLeft > 0;
      this.canScrollRight = container.scrollLeft < (container.scrollWidth - container.clientWidth - 1); // Added -1 for better detection
      this.cdr.detectChanges();
    }
  }

  scrollRecentItems(direction: 'left' | 'right') {
    const container = this.itemsContainer.nativeElement;
    const scrollAmount = 200; // Adjust this value based on your needs

    // Add sliding class
    container.classList.add('sliding');

    if (direction === 'left') {
      container.scrollLeft -= scrollAmount;
    } else {
      container.scrollLeft += scrollAmount;
    }

    // Check scrollability after animation
    setTimeout(() => {
      this.checkScrollability();
      // Remove sliding class after animation
      container.classList.remove('sliding');
      this.cdr.detectChanges();
    }, 300);
  }
  isScrolled = false;
  isClientPageScrolled = false; // Add separate property for client pages
  
  @HostListener('window:scroll')
  onWindowScroll() {
    const currentUrl = this.router.url;
    
    // Use the same scrolled logic for all pages including client pages
    this.isScrolled = window.pageYOffset > -10;
    this.isClientPageScrolled = false; // Reset client scroll state for all pages
    
    this.cdr.detectChanges();
  }

  @HostListener('window:resize')
  onWindowResize() {
    // Recheck scrollability when window is resized
    setTimeout(() => {
      this.checkScrollability();
      this.checkForArrows();
      this.cdr.detectChanges();
    }, 100);
  }

  toggleNav() {
    this.navActive = !this.navActive;
    const navLinks = document.querySelector('.nav-links');
    const toggleButton = document.querySelector('.mobile-nav-toggle');

    if (navLinks) {
      if (this.navActive) {
        navLinks.classList.add('active');
        toggleButton?.classList.add('active');
        // Add click-outside listener
        setTimeout(() => {
          const handler = this.handleNavLinksOutsideClick as EventListener;
          document.addEventListener('click', handler, true);
          this.clickListeners.push({ element: document, event: 'click', handler });
        });
      } else {
        navLinks.classList.remove('active');
        toggleButton?.classList.remove('active');
        // Remove click-outside listener
        const handler = this.handleNavLinksOutsideClick as EventListener;
        document.removeEventListener('click', handler, true);
        this.clickListeners = this.clickListeners.filter(
          listener => !(listener.element === document && listener.handler === handler)
        );
      }
    }
    this.cdr.detectChanges();
  }

  // Handler to close nav-links when clicking outside
  handleNavLinksOutsideClick = (event: MouseEvent) => {
    const navLinks = document.querySelector('.nav-links');
    const toggleButton = document.querySelector('.mobile-nav-toggle');
    if (
      navLinks &&
      !navLinks.contains(event.target as Node) &&
      toggleButton &&
      !toggleButton.contains(event.target as Node)
    ) {
      navLinks.classList.remove('active');
      toggleButton.classList.remove('active');
      this.navActive = false;
      document.removeEventListener('click', this.handleNavLinksOutsideClick, true);
      this.cdr.detectChanges();
    }
  };

  onSearch() {
    // Navigate to search page with query parameters
    if (this.searchQuery().trim()) {
      this.router.navigate(['/search'], {
        queryParams: {
          q: this.searchQuery().trim(),
          category: this.selectedSearchOption()
        }
      });
    } else {
      // If no search query, just navigate with the selected category
      this.router.navigate(['/search'], {
        queryParams: {
          category: this.selectedSearchOption()
        }
      });
    }
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
    // Save theme mode to API
    this.themeManagementService.saveThemeMode().subscribe({
      next: (response) => {
        // if (response.success) {
        //   console.log('Theme mode saved successfully');
        // } else {
        //   console.error('Failed to save theme mode:', response.error || 'Unknown error');
        // }
      },
      error: (error) => {
        console.error('Error saving theme mode:', error);
      }
    });
    this.cdr.detectChanges();
  }
  getUserProfiledata(userId?: string): void {
    // If userId is not provided, use the one from localStorage 
    const id = userId || localStorage.getItem('userID');
    localStorage.removeItem('userThemeMode');
    localStorage.removeItem('selectedBackgroundTheme');
    this.apiService.getuserProfileData(id || '').subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.userName = response.data.name || '';
          if (response.data.theme) {
            localStorage.setItem('selectedBackgroundTheme', response.data.theme);
          }
          if (response.data.mode) {
            localStorage.setItem('userThemeMode', response.data.mode);
          }
          // Update profile image if available in API response
          if (response.data.profilePhoto || response.data.image) {
            const profileImage = response.data.image;
            this.updateUserImage(profileImage);
            localStorage.setItem('userImage', profileImage ?? '');
          } else {
            // Explicitly clear when no image is available
            this.updateUserImage('');
            localStorage.setItem('userImage', '');
          }
          
         
          // console.log('User profile data loaded:', response.data);
        }
      },
      error: (error) => {
        console.error('Error fetching user profile data:', error);
      }
    }); 
   }

  toggleSearchDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.searchDropdownOpen.set(!this.searchDropdownOpen());
    this.cdr.detectChanges();

    if (this.searchDropdownOpen()) {
      const closeDropdown: EventListener = () => {
        this.searchDropdownOpen.set(false);
        this.cdr.detectChanges();
        document.removeEventListener('click', closeDropdown);
        this.clickListeners = this.clickListeners.filter(
          listener => !(listener.element === document && listener.handler === closeDropdown)
        );
      };
      document.addEventListener('click', closeDropdown);
      this.clickListeners.push({ element: document, event: 'click', handler: closeDropdown });
    }
  }

  selectSearchOption(option: string): void {
    this.selectedSearchOption.set(option);
    this.searchCategoryService.setSelectedCategory(option);
    this.searchDropdownOpen.set(false);
    this.cdr.detectChanges();
    
  }

  toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.dropdownOpen.set(!this.dropdownOpen());
    this.cdr.detectChanges();
    this.isDropdownOpen = !this.isDropdownOpen;

    // Emit dropdown state to layout component
    this.profileDropdownStateChange.emit({
      isOpen: this.dropdownOpen(),
      userName: this.userName,
      userImage: this.userImage
    });

    if (this.dropdownOpen()) {
      const closeDropdown: EventListener = () => {
        this.dropdownOpen.set(false);
        this.cdr.detectChanges();
        this.isDropdownOpen = false;
        // Emit closed state
        this.profileDropdownStateChange.emit({
          isOpen: false,
          userName: this.userName,
          userImage: this.userImage
        });
        document.removeEventListener('click', closeDropdown);
        this.clickListeners = this.clickListeners.filter(
          listener => !(listener.element === document && listener.handler === closeDropdown)
        );
      };
      document.addEventListener('click', closeDropdown);
      this.clickListeners.push({ element: document, event: 'click', handler: closeDropdown });
    }
  }

  toggleNotifications(event: MouseEvent): void {
    event.stopPropagation();
    
    // Clean up any existing notification click listeners
    this.cleanupNotificationListeners();
    
    this.notificationsOpen.set(!this.notificationsOpen());
    this.notificationService.setPanelOpen(this.notificationsOpen());
    this.cdr.detectChanges();

    if (this.notificationsOpen()) {
      // Add a small delay to ensure the dropdown is rendered in the DOM
      setTimeout(() => {
        const closeNotifications: EventListener = (e: Event) => {
          // Check if click is outside notifications panel
          const target = e.target as HTMLElement;
          const notificationsPanel = document.querySelector('.notification-dropdown');
          const notificationsToggle = document.querySelector('.notifications-toggle');
          
          // Don't close if clicking on the dropdown itself or the toggle button
          if (notificationsPanel && !notificationsPanel.contains(target) && 
              notificationsToggle && !notificationsToggle.contains(target)) {
            this.notificationsOpen.set(false);
            this.notificationService.setPanelOpen(false);
            this.cdr.detectChanges();
            this.cleanupNotificationListeners();
          }
        };
        document.addEventListener('click', closeNotifications);
        this.clickListeners.push({ element: document, event: 'click', handler: closeNotifications });
      }, 10);
    }
  }

  private cleanupNotificationListeners(): void {
    // Remove all click listeners for notifications
    this.clickListeners = this.clickListeners.filter(listener => {
      if (listener.element === document && listener.event === 'click') {
        document.removeEventListener('click', listener.handler);
        return false; // Remove from array
      }
      return true; // Keep in array
    });
  }

  closeNotificationDropdown(): void {
    this.notificationsOpen.set(false);
    this.notificationService.setPanelOpen(false);
    this.cleanupNotificationListeners();
    this.cdr.detectChanges();
  }

  onViewAllNotifications(): void {
    this.closeNotificationDropdown();
    
    // Small delay to ensure dropdown closes before navigation
    setTimeout(() => {
      this.router.navigate(['/notifications']);
    }, 100);
  }

  onNotificationClicked(): void {
    // Close the dropdown when a notification is clicked
    this.closeNotificationDropdown();
  }

  loadRecentItems() {
    this.apiService.getRecentlyViewed().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          // Frontend fallback: Move last edited project to top if present
          const lastEditedProjectId = localStorage.getItem('lastEditedProjectId');
          const lastEditedProjectName = localStorage.getItem('lastEditedProjectName');
          let items = response.data;
          if (lastEditedProjectId) {
            const idx = items.findIndex((item: any) => item._id === lastEditedProjectId);
            if (idx > -1) {
              if (lastEditedProjectName) {
                items[idx].projectName = lastEditedProjectName;
              }
              if (idx > 0) {
                const [edited] = items.splice(idx, 1);
                items.unshift(edited);
              }
            }
            localStorage.removeItem('lastEditedProjectId');
            localStorage.removeItem('lastEditedProjectName');
          }
          // Remove duplicate projects by _id, keeping the first occurrence
          const seen = new Set();
          items = items.filter((item: any) => {
            if (seen.has(item._id)) return false;
            seen.add(item._id);
            return true;
          });
          this.recentItems.set(items);
          // console.log('Recent items loaded:', this.recentItems());
          // Check scrollability after data is loaded
          setTimeout(() => {
            this.checkScrollability();
            this.checkForArrows();
            this.setupScrollListener();
            this.cdr.detectChanges();
          }, 100);
        }
      },
      error: (error) => {
        console.error('Error fetching recent items:', error);
      }
    });
  }

  onRecentItemClick(item: { _id: string, projectName: string }, event: Event) {
    // Only prevent default for left-click to allow right-click context menu
    if (event.type === 'click') {
      event.preventDefault(); // Prevent default link behavior for left-click

      // Open the item's page in a new tab programmatically
      const url = `#/projects/project-details/${item._id}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    // For right-click and other events, let the default link behavior work
  }


  logout(): void {
    // Preserve theme data before clearing localStorage
    const savedTheme = localStorage.getItem('selectedBackgroundTheme');
    const isThemeActive = localStorage.getItem('theme-background-active') === 'true';

    // Clear theme-background-active class to prevent blurred login page
    document.body.classList.remove('theme-background-active');
    document.body.style.backgroundImage = '';
    this.userSettingsService.clearSettings();
    this.authService.logout();
    localStorage.clear();

    // Restore theme data for next login if it was active
    if (savedTheme && isThemeActive) {
      localStorage.setItem('selectedBackgroundTheme', savedTheme);
      localStorage.setItem('theme-background-active', 'true');
    }
  }


  checkForArrows() {
    // Check if there are enough items to scroll
    const container = this.itemsContainer?.nativeElement;
    if (container && this.recentItems().length > 0) {
      // Show arrows if content is wider than container
      this.showRecentArrows = container.scrollWidth > container.clientWidth;
      this.cdr.detectChanges();
    } else {
      this.showRecentArrows = false;
      this.cdr.detectChanges();
    }
  }

  onHeaderClick(header: NavigationItem): void {
    this.navigationService.setActiveHeader(header);
    this.router.navigate([header.to]);
    // Remove active class from nav-links on mobile
    if (window.innerWidth <= 1199) {
      const navLinks = document.querySelector('.nav-links');
      const toggleButton = document.querySelector('.mobile-nav-toggle');
      navLinks?.classList.remove('active');
      toggleButton?.classList.remove('active');
      this.navActive = false;
      document.removeEventListener('click', this.handleNavLinksOutsideClick, true);
    }
    this.cdr.detectChanges();
  }

  isHeaderActive(header: NavigationItem): boolean {
    // Use the navigation service's logic exclusively to prevent conflicts
    return this.navigationService.isHeaderActive(header);
  }

  toggleSidebar() {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
    this.layoutService.toggleSidebar();
  }

  onThemeBtnClick() {
    this.themeBtnClick.emit();
  }



  toggleChangePasswordForm() {
    this.changePasswordClick.emit();
  }

  toggleRecentlyViewed() {
    this.recentlyViewedExpanded.set(!this.recentlyViewedExpanded());
    this.cdr.detectChanges();
  }

  onClientSummaryClick(): void {
    this.showClientSummarySlider = true;
  }

  onCloseClientSummarySlider(): void {
    this.showClientSummarySlider = false;
  }
  getCurrentContext(): { objCode: string, objID: string } {
    const currentUrl = this.router.url;
    const urlParts = currentUrl.split('/').filter(Boolean); // remove empty parts
  
    // 🔹 PROJECT context
    const projectIndex = urlParts.findIndex(
      part => part === 'projects' || part === 'project-details'
    );
  
    if (projectIndex !== -1) {
      // Handle cases like:
      // /projects/project-details/:id
      // /project-details/:id
      const possibleId = urlParts[projectIndex + 1] === 'project-details'
        ? urlParts[projectIndex + 2]
        : urlParts[projectIndex + 1];
  
      if (possibleId) {
        return {
          objCode: 'PROJECT',
          objID: possibleId
        };
      }
    }
  
    // 🔹 CLIENT context
    const clientIndex = urlParts.findIndex(
      part => part === 'clients' || part === 'client-details'
    );
  
    if (clientIndex !== -1) {
      const possibleId = urlParts[clientIndex + 1] === 'client-details'
        ? urlParts[clientIndex + 2]
        : urlParts[clientIndex + 1];
  
      if (possibleId) {
        return {
          objCode: 'PORTFOLIO',
          objID: possibleId
        };
      }
    }
  
    // 🔹 Fallback to stored context
    const storedClientId = localStorage.getItem('currentClientId');
    const storedProjectId = localStorage.getItem('currentProjectId');
  
    if (storedProjectId) {
      return { objCode: 'PROJECT', objID: storedProjectId };
    }
  
    if (storedClientId) {
      return { objCode: 'PORTFOLIO', objID: storedClientId };
    }
  
    // 🔹 Final fallback
    return { objCode: 'PORTFOLIO', objID: 'default-client-id' };
  }
  
  expandSearchSection(): void {
    // Collapse the recently viewed section to expand search section
    this.recentlyViewedExpanded.set(false);
    this.cdr.detectChanges();
  }

  onSupportClick(): void {
    this.router.navigate(['/support']);
  }

  onSettingsClick(): void {
    // Emit event to parent layout component
    this.settingsClick.emit();
  }

  onSettingsSaved(settings: UserSettings): void {
    // Update local user data if needed
    if (settings.name) {
      this.userName = settings.name;
      localStorage.setItem('userName', settings.name);
    }
    // Update profile image if changed
    if (settings.profilePhoto !== undefined) {
      this.updateUserImage(settings.profilePhoto);
    }
  }

  private preloadUserSettings(): void {
    // Only preload if settings are not already loaded
    if (!this.userSettingsService.areSettingsLoaded() && !this.userSettingsService.areSettingsLoading()) {
      this.userSettingsService.loadUserSettings().subscribe({
        next: (response) => {
          this.userSettingsService.processUserSettings(response);
        },
        error: (error) => {
          console.error('Error preloading user settings:', error);
        }
      });
    }
  }

  onPeopleClick(): void {
    this.router.navigate(['/people']);
  }

  toggleSearchDisabled(): void {
    this.searchDisabled.set(!this.searchDisabled());
    this.cdr.detectChanges();
  }

  navigateToUserProfile(event: MouseEvent): void {
    event.stopPropagation();
    if (this.userId) {
      this.dropdownOpen.set(false);
      this.isDropdownOpen = false;
      this.router.navigate(['/people/peopleview', this.userId]);
    }
  }


  enableSearch(): void {
    this.searchDisabled.set(false);
    this.cdr.detectChanges();
  }

  disableSearch(): void {
    this.searchDisabled.set(true);
    this.cdr.detectChanges();
  }



  toggleMoreDropdown(event: MouseEvent): void {
    event.stopPropagation();
    
    // Toggle the dropdown for all devices
    this.moreDropdownOpen.set(!this.moreDropdownOpen());
    this.cdr.detectChanges();

    if (this.moreDropdownOpen()) {
      const closeDropdown: EventListener = () => {
        this.moreDropdownOpen.set(false);
        this.cdr.detectChanges();
        document.removeEventListener('click', closeDropdown);
        this.clickListeners = this.clickListeners.filter(
          listener => !(listener.element === document && listener.handler === closeDropdown)
        );
      };
      document.addEventListener('click', closeDropdown);
      this.clickListeners.push({ element: document, event: 'click', handler: closeDropdown });
    }
  }

  onMoreItemClick(item: string): void {
    this.moreDropdownOpen.set(false);
    this.cdr.detectChanges();
    
    // Handle navigation based on item
    switch (item) {
      case 'Queues':
        this.router.navigate(['/queues']);
        break;
      case 'Reports':
        this.router.navigate(['/reports']);
        break;
      case 'Analytics':
        this.router.navigate(['/analytics']);
        break;
      case 'Settings':
        this.router.navigate(['/settings']);
        break;
      case 'Help':
        this.router.navigate(['/help']);
        break;
      default:
        console.log('Unknown more item:', item);
    }
  }

  // Method to update user image
  private updateUserImage(imageUrl: string): void {
    this.profileimage = imageUrl;
    this.userImage = (imageUrl && imageUrl.trim()) ? imageUrl : 'assets/images/avatar.png';
    this.cdr.detectChanges();
  }

  // Method to handle avatar image error and fallback to default
  onAvatarImageError(): void {
    this.userImage = 'assets/images/avatar.png';
    this.cdr.detectChanges();
  }

  // Public method to refresh user image from localStorage
  public refreshUserImage(): void {
    const storedImage = localStorage.getItem('userImage');
    this.updateUserImage(storedImage || '');
  }

  // Method to force update scroll state based on current route
  private updateScrollStateForRoute(): void {
    const currentUrl = this.router.url;
    if (currentUrl.includes('/clients') || currentUrl.includes('/clients-list')) {
      this.isScrolled = false;
      this.isClientPageScrolled = false; // Reset client scroll state
      this.cdr.detectChanges();
      
      // Remove scrolled class from DOM
      setTimeout(() => {
        const headerElement = document.querySelector('.header-menu');
        if (headerElement) {
          headerElement.classList.remove('scrolled');
        }
      }, 0);
    } else {
      // Reset client scroll state for non-client pages
      this.isClientPageScrolled = false;
    }
  }

  // Method to update client summary button visibility
  private updateClientSummaryButtonVisibility(): void {
    const currentUrl = this.router.url;
    this.showClientSummaryButton = this.allowedRoutes.some(route =>
      currentUrl.includes(route)
    );
    this.cdr.detectChanges();
  }

  // Footer menu methods
  onFooterMenuClick(menuItem: string): void {
    switch (menuItem) {
      case 'dashboard':
        this.router.navigate(['/dashboard']);
        break;
      case 'requests':
        this.router.navigate(['/requests']);
        break;
      case 'timesheets':
        this.router.navigate(['/timesheets']);
        break;
      case 'history':
        this.router.navigate(['/history']);
        break;
      default:
        console.log('Unknown footer menu item:', menuItem);
    }
  }

  isFooterMenuActive(menuItem: string): boolean {
    const currentUrl = this.router.url;
    
    switch (menuItem) {
      case 'dashboard':
        // Only match exact dashboard routes
        return currentUrl === '/' || currentUrl === '/dashboard';
      case 'requests':
        // Only match the main requests page, not sub-routes or other request-related pages
        return currentUrl === '/requests';
      case 'timesheets':
        // Match timesheets page and any sub-routes
        return currentUrl === '/timesheets' || currentUrl.startsWith('/timesheets/');
      case 'history':
        // Match history page and any sub-routes
        return currentUrl === '/history' || currentUrl.startsWith('/history/');
      default:
        return false;
    }
  }

}

